import { useState, useEffect, useRef, useContext } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ArrowRight, ArrowLeft, User, FileText, Link as LinkIcon, 
  Instagram, Facebook, Globe, Loader2, Upload, X, Camera
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { toast } from "sonner";
import axios from "axios";
import { API, AuthContext } from "../../App";

const OnboardingProfileContent = () => {
  const navigate = useNavigate();
  const { onboardingData, refreshData } = useOutletContext();
  const { refreshUser } = useContext(AuthContext);
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const avatarInputRef = useRef(null);
  const logoInputRef = useRef(null);

  const [tagline, setTagline] = useState("");
  const [bio, setBio] = useState("");
  const [socialLinks, setSocialLinks] = useState({
    instagram: "",
    facebook: "",
    website: "",
    tiktok: "",
    twitter: ""
  });
  const [showEmail, setShowEmail] = useState(true);
  const [showPhone, setShowPhone] = useState(false);
  const [phone, setPhone] = useState("");
  const [avatarUrl, setAvatarUrl] = useState("");
  const [logoUrl, setLogoUrl] = useState("");

  useEffect(() => {
    if (onboardingData) {
      const p = onboardingData.photographer;
      const u = onboardingData.user;
      
      setTagline(p.tagline || "");
      setBio(p.bio || "");
      setSocialLinks({
        instagram: p.social_links?.instagram || "",
        facebook: p.social_links?.facebook || "",
        website: p.social_links?.website || "",
        tiktok: p.social_links?.tiktok || "",
        twitter: p.social_links?.twitter || ""
      });
      setShowEmail(p.show_email !== false);
      setShowPhone(p.show_phone || false);
      setPhone(p.phone || "");
      setAvatarUrl(u.picture || "");
      setLogoUrl(p.logo_url || "");
    }
  }, [onboardingData]);

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingAvatar(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(`${API}/photographer/avatar`, formData, {
        headers: { 
          "Content-Type": "multipart/form-data",
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        }
      });
      setAvatarUrl(response.data.picture);
      toast.success("Profile photo uploaded!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload photo");
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingLogo(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(`${API}/photographer/logo`, formData, {
        headers: { 
          "Content-Type": "multipart/form-data",
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        }
      });
      setLogoUrl(response.data.logo_url);
      toast.success("Logo uploaded!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload logo");
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleContinue = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      await axios.put(`${API}/onboarding/step/profile-content`, {
        tagline: tagline || null,
        bio: bio || null,
        social_links: socialLinks,
        show_email: showEmail,
        show_phone: showPhone,
        phone: phone || null
      }, { headers });
      
      toast.success("Profile content saved!");
      await refreshData();
      if (refreshUser) await refreshUser();
      navigate("/dashboard/onboarding/portfolio");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate("/dashboard/onboarding");
  };

  const getAvatarSrc = () => {
    if (!avatarUrl) return null;
    if (avatarUrl.startsWith("http")) return avatarUrl;
    return `${API.replace("/api", "")}${avatarUrl}`;
  };

  const getLogoSrc = () => {
    if (!logoUrl) return null;
    return `${API.replace("/api", "")}${logoUrl}`;
  };

  return (
    <div data-testid="onboarding-step-2">
      <div className="mb-8">
        <h1 className="text-3xl font-serif mb-2">Profile Content</h1>
        <p className="text-muted-foreground">
          Add details that help clients learn about you. All fields are optional.
        </p>
      </div>

      <div className="space-y-6">
        {/* Photo & Logo */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Camera className="w-5 h-5" />
              Photos
            </CardTitle>
            <CardDescription>Add a profile photo and/or business logo</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row gap-6">
              {/* Profile Photo */}
              <div className="flex-1">
                <Label className="mb-2 block">Profile Photo</Label>
                <div className="flex items-center gap-4">
                  <div className="relative">
                    {getAvatarSrc() ? (
                      <img
                        src={getAvatarSrc()}
                        alt="Profile"
                        className="w-20 h-20 rounded-full object-cover border-2 border-border"
                      />
                    ) : (
                      <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center border-2 border-dashed border-border">
                        <User className="w-8 h-8 text-muted-foreground" />
                      </div>
                    )}
                    {uploadingAvatar && (
                      <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center">
                        <Loader2 className="w-6 h-6 animate-spin text-white" />
                      </div>
                    )}
                  </div>
                  <div>
                    <input
                      ref={avatarInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleAvatarUpload}
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => avatarInputRef.current?.click()}
                      disabled={uploadingAvatar}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Upload
                    </Button>
                  </div>
                </div>
              </div>

              {/* Logo */}
              <div className="flex-1">
                <Label className="mb-2 block">Business Logo</Label>
                <div className="flex items-center gap-4">
                  <div className="relative">
                    {getLogoSrc() ? (
                      <img
                        src={getLogoSrc()}
                        alt="Logo"
                        className="h-20 max-w-[120px] object-contain border-2 border-border rounded-lg p-2"
                      />
                    ) : (
                      <div className="w-20 h-20 rounded-lg bg-muted flex items-center justify-center border-2 border-dashed border-border">
                        <FileText className="w-8 h-8 text-muted-foreground" />
                      </div>
                    )}
                    {uploadingLogo && (
                      <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center">
                        <Loader2 className="w-6 h-6 animate-spin text-white" />
                      </div>
                    )}
                  </div>
                  <div>
                    <input
                      ref={logoInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleLogoUpload}
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => logoInputRef.current?.click()}
                      disabled={uploadingLogo}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Upload
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tagline & Bio */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              About You
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="tagline">Tagline</Label>
              <Input
                id="tagline"
                placeholder="e.g., Capturing life's beautiful moments"
                value={tagline}
                onChange={(e) => setTagline(e.target.value)}
                maxLength={100}
              />
              <p className="text-xs text-muted-foreground">{tagline.length}/100 characters</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="bio">Bio</Label>
              <Textarea
                id="bio"
                placeholder="Tell clients about yourself, your experience, and what makes your photography special..."
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                rows={4}
                maxLength={500}
              />
              <p className="text-xs text-muted-foreground">{bio.length}/500 characters</p>
            </div>
          </CardContent>
        </Card>

        {/* Social Links */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <LinkIcon className="w-5 h-5" />
              Social Links
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Instagram className="w-4 h-4" /> Instagram
                </Label>
                <Input
                  placeholder="@username or full URL"
                  value={socialLinks.instagram}
                  onChange={(e) => setSocialLinks(prev => ({ ...prev, instagram: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Facebook className="w-4 h-4" /> Facebook
                </Label>
                <Input
                  placeholder="facebook.com/yourpage"
                  value={socialLinks.facebook}
                  onChange={(e) => setSocialLinks(prev => ({ ...prev, facebook: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Globe className="w-4 h-4" /> Website
                </Label>
                <Input
                  placeholder="www.yourwebsite.com"
                  value={socialLinks.website}
                  onChange={(e) => setSocialLinks(prev => ({ ...prev, website: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>TikTok</Label>
                <Input
                  placeholder="@username"
                  value={socialLinks.tiktok}
                  onChange={(e) => setSocialLinks(prev => ({ ...prev, tiktok: e.target.value }))}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Contact Visibility */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle>Contact Visibility</CardTitle>
            <CardDescription>Choose what contact info to show on your profile</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label>Show Email</Label>
                <p className="text-xs text-muted-foreground">{onboardingData?.user?.email}</p>
              </div>
              <Switch checked={showEmail} onCheckedChange={setShowEmail} />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label>Show Phone</Label>
                {showPhone && (
                  <Input
                    placeholder="(555) 555-5555"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="mt-2 w-48"
                  />
                )}
              </div>
              <Switch checked={showPhone} onCheckedChange={setShowPhone} />
            </div>
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex justify-between pt-4">
          <Button variant="outline" onClick={handleBack}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <Button onClick={handleContinue} disabled={saving} data-testid="continue-btn">
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default OnboardingProfileContent;
